<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useCurrentStore } from "@/stores/current";
import { useWebRTCStore } from "@/stores/webrtc";
import { useEventStore } from "@/stores/event";
import WarningPopup from "@cmp/WarningPopup.vue";
import ChannelCard from "@cmp/ChannelCard.vue";
import { useI18nStore } from "@/stores/i18n";

const event = computed(() => useEventStore().getEvent);
const languages = computed(() => {
  const eventLanguages = event.value?.languages;
  if (eventLanguages) return eventLanguages;
  const defaultLocale = useI18nStore().getDefaultLocale;
  return [defaultLocale];
});
const eventChannels = computed(() => event.value?.channels || []);

const audioWss = import.meta.env.VITE_AUDIO_WSS;

onMounted(async () => {
  useWebRTCStore()
    .initialize(audioWss)
    .then(() => {});
  setTimeout(changeTexts, 500);
});

onUnmounted(() => {
  if (changeTextsTimeout.value) clearTimeout(changeTextsTimeout.value);
});

const languageIndex = ref(0);
const changeTextsTimeout = ref(null);

function changeTexts() {
  if (languages.value.length > 1) opacity0.value = true;
  setTimeout(() => {
    useI18nStore().setLocale(languages.value[languageIndex.value++]);
    languageIndex.value = languageIndex.value % languages.value.length;
    if (languages.value.length > 1) opacity0.value = false;
    changeTextsTimeout.value = setTimeout(changeTexts, 3600);
  }, 300);
}

const router = useRouter();

const opacity0 = ref(false);
const popup = ref(false);
const channelSelected = ref(null);

function openPopup(channel) {
  if (changeTextsTimeout.value) clearTimeout(changeTextsTimeout.value);
  useI18nStore().setLocale(channel.language);
  channelSelected.value = channel;
  popup.value = true;
  useCurrentStore().setPopupOpen(true);
}

function closePopup() {
  popup.value = false;
  useCurrentStore().setPopupOpen(false);
  changeTexts();
}

function goToLang() {
  useCurrentStore().setChannel(channelSelected.value);
  useCurrentStore().setPopupOpen(false);
  router.push({ name: "listen" });
}
</script>

<template>
  <div class="flex flex-col items-center mt-5">
    <WarningPopup
      v-if="popup"
      :button-text="$t('channels.popup.button')"
      :text="$t('channels.popup.body')"
      :title="$t('channels.popup.title')"
      type="warning"
      @close="closePopup"
      @next="goToLang"
    />
    <div class="relative w-full flex flex-col px-5 max-w-xl">
      <div class="relative">
        <h1
          :class="{ 'opacity-0': opacity0, 'opacity-100': !opacity0 }"
          class="font-bold whitespace-pre-line text-3xl mb-10 transition-opacity ease-in duration-300 h-20"
        >
          {{ $t("channels.welcome") }}
        </h1>
      </div>
      <p
        :class="{ 'opacity-0': opacity0, 'opacity-100': !opacity0 }"
        class="text-lg whitespace-pre-line mb-5 transition-opacity ease-in duration-300"
      >
        {{ $t("channels.help") }}
      </p>

      <ChannelCard
        v-for="channel in eventChannels"
        :key="channel.id"
        :id="channel.id"
        :name="channel.name"
        :image="channel.image"
        :language="channel.language"
        @click="openPopup(channel)"
      />
    </div>
  </div>
</template>
